// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/eventTime.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/eventTime.js
//
// When an event actually happens, in a world with guests in more than one
// time zone.
//
// ── The model ──────────────────────────────────────────────────────────────
//
// events.event_date / event_time / end_date / end_time are the event's own
// wall-clock time: what the host typed, '2026-10-17' and '13:00:00'. Until
// now nothing said WHICH clock - so a guest in New York read an Indian 4:45 PM
// as their own 4:45 PM, and "has it ended?" was asked of the guest's clock,
// up to ten hours out. events.timezone (IANA, e.g. 'Asia/Kolkata') says which
// clock it is. With it:
//
//   - the event is shown in ITS local time, to everyone - as the host wrote it;
//   - a guest somewhere else also sees the event's zone, and one tap shows the
//     time in their own (eventTimeDisplay), as Partiful does;
//   - "ended", deadlines and anything else that needs the real moment use
//     eventStartMs / eventEndMs / isEventOver, not the viewer's clock.
//
// An event without a timezone (made before it existed, or by an old app) is
// read on the viewer's own clock - exactly the old behaviour.
//
// Pure and import-free: the app (Hermes), the website and the tests share it.
// Every Intl call is guarded, so an engine without time-zone support falls
// back to the old behaviour instead of throwing.

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/;

const parseDate = (v) => {
  const m = DATE_RE.exec(String(v ?? '').trim());
  return m ? { y: +m[1], mo: +m[2], d: +m[3] } : null;
};
const parseTime = (v) => {
  const m = TIME_RE.exec(String(v ?? '').trim());
  return m ? { h: +m[1], mi: +m[2], s: +(m[3] ?? 0) } : null;
};

// ── Zones ──────────────────────────────────────────────────────────────────

const formatters = new Map();
function partsFormatter(timeZone) {
  if (!formatters.has(timeZone)) {
    formatters.set(timeZone, new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }));
  }
  return formatters.get(timeZone);
}

/** Whether `timeZone` is a zone this engine knows. */
export function isValidTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== 'string') return false;
  try {
    partsFormatter(timeZone);
    return true;
  } catch {
    formatters.delete(timeZone);
    return false;
  }
}

/** The device's own zone, or null. */
export function deviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * How far `timeZone`'s clock is ahead of UTC at instant `atMs`, in ms
 * (IST: +19800000). Null when it cannot be told.
 */
export function zoneOffsetMs(timeZone, atMs) {
  if (!isValidTimeZone(timeZone)) return null;
  try {
    const p = {};
    for (const part of partsFormatter(timeZone).formatToParts(new Date(atMs))) p[part.type] = part.value;
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
    const whole = Math.floor(atMs / 1000) * 1000;
    return asUtc - whole;
  } catch {
    return null;
  }
}

/**
 * The instant at which `timeZone`'s clock reads the given wall-clock time.
 * Without a usable zone, the engine's local clock (the old behaviour).
 */
export function wallClockToMs({ y, mo, d, h = 0, mi = 0, s = 0 }, timeZone) {
  const guess = Date.UTC(y, mo - 1, d, h, mi, s);
  const first = zoneOffsetMs(timeZone, guess);
  if (first == null) return new Date(y, mo - 1, d, h, mi, s).getTime();
  let ms = guess - first;
  // Across a daylight-saving change the offset at the answer can differ from
  // the offset at the guess; one more pass settles it.
  const second = zoneOffsetMs(timeZone, ms);
  if (second != null && second !== first) ms = guess - second;
  return ms;
}

/** Whether two zones' clocks agree at `atMs` (Asia/Calcutta and Asia/Kolkata do). */
export function sameClock(zoneA, zoneB, atMs = Date.now()) {
  if (!zoneA || !zoneB) return true;
  if (zoneA === zoneB) return true;
  const a = zoneOffsetMs(zoneA, atMs);
  const b = zoneOffsetMs(zoneB, atMs);
  return a == null || b == null || a === b;
}

// Zones whose usual abbreviation no locale in Intl produces.
const LABELS = {
  'Asia/Singapore': 'SGT', 'Asia/Kuala_Lumpur': 'MYT', 'Asia/Kathmandu': 'NPT',
  'Asia/Katmandu': 'NPT', 'Asia/Dhaka': 'BST', 'Asia/Karachi': 'PKT', 'Asia/Colombo': 'IST',
  'Asia/Hong_Kong': 'HKT', 'Asia/Manila': 'PHT', 'Asia/Jakarta': 'WIB', 'Asia/Bangkok': 'ICT',
  'Asia/Riyadh': 'AST', 'Asia/Qatar': 'AST', 'Asia/Kuwait': 'AST', 'Asia/Bahrain': 'AST',
  'Asia/Muscat': 'GST', 'Asia/Tokyo': 'JST', 'Asia/Seoul': 'KST', 'Asia/Shanghai': 'CST',
  'Africa/Johannesburg': 'SAST', 'Africa/Lagos': 'WAT', 'Africa/Nairobi': 'EAT',
};

function zoneName(timeZone, atMs, locale, style) {
  try {
    const part = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: style })
      .formatToParts(new Date(atMs))
      .find((p) => p.type === 'timeZoneName');
    return part?.value || null;
  } catch {
    return null;
  }
}

/**
 * A short label for a zone: ET, PT, IST, BST, AEDT, SGT… or GMT+8 when no
 * abbreviation is in common use. US zones get the generic form (ET, not
 * EDT), as Partiful shows them.
 */
export function zoneLabel(timeZone, atMs = Date.now()) {
  if (!isValidTimeZone(timeZone)) return '';
  const generic = zoneName(timeZone, atMs, 'en-US', 'shortGeneric');
  if (generic && /^[A-Z]{1,4}$/.test(generic)) return generic;
  if (LABELS[timeZone]) return LABELS[timeZone];
  for (const locale of ['en-US', 'en-IN', 'en-GB', 'en-AU', 'en-CA', 'en-NZ']) {
    const short = zoneName(timeZone, atMs, locale, 'short');
    if (short && !/^(GMT|UTC)/.test(short)) return short;
  }
  return zoneName(timeZone, atMs, 'en-US', 'short') || timeZone;
}

/** 'Asia/Kolkata' -> 'Kolkata', 'America/New_York' -> 'New York'. */
export function zoneCity(timeZone) {
  const last = String(timeZone || '').split('/').pop() || '';
  return last.replace(/_/g, ' ');
}

// ── Events ─────────────────────────────────────────────────────────────────

/** The event's zone, when it has a usable one. */
export function eventTimeZone(event) {
  return isValidTimeZone(event?.timezone) ? event.timezone : null;
}

/** When the event starts, in epoch ms, or null. */
export function eventStartMs(event) {
  const date = parseDate(event?.event_date);
  if (!date) return null;
  const time = parseTime(event?.event_time) ?? { h: 0, mi: 0, s: 0 };
  return wallClockToMs({ ...date, ...time }, eventTimeZone(event));
}

/**
 * When the event is over, in epoch ms, or null.
 *
 * Its end date and time when it has them. An end time with no end date that
 * reads earlier than the start (10 pm - 2 am) is the next morning. No end time
 * at all: the end of its last day - an event that has just begun has not
 * "ended", and guests can still RSVP on the day.
 */
export function eventEndMs(event) {
  const start = parseDate(event?.event_date);
  const endDate = parseDate(event?.end_date);
  const date = endDate ?? start;
  if (!date) return null;
  const tz = eventTimeZone(event);

  const endTime = parseTime(event?.end_time);
  if (!endTime) return wallClockToMs({ ...date, h: 23, mi: 59, s: 59 }, tz);

  let ms = wallClockToMs({ ...date, ...endTime }, tz);
  const startTime = parseTime(event?.event_time);
  if (!endDate && startTime && (endTime.h * 60 + endTime.mi) < (startTime.h * 60 + startTime.mi)) {
    ms = wallClockToMs({ ...date, d: date.d + 1, ...endTime }, tz);
  }
  return ms;
}

/** Whether the event is over. Unknown timing is never "over". */
export function isEventOver(event, nowMs = Date.now()) {
  const end = eventEndMs(event);
  return end != null && end < nowMs;
}

// ── Showing it ─────────────────────────────────────────────────────────────

function fmt(ms, timeZone, options, locale) {
  try {
    return new Date(ms).toLocaleString(locale, timeZone ? { ...options, timeZone } : options);
  } catch {
    return new Date(ms).toLocaleString(locale, options);
  }
}

/**
 * What a viewer needs to show the event's time the way Partiful does.
 *
 *   sameClock        the viewer is on the event's clock: show the time plainly
 *   eventLabel       the event's zone (ET, IST…), shown beside its time
 *   viewerLabel      the viewer's zone, the tap target
 *   viewerDate/Time  the start in the viewer's zone ('Sat, Oct 17', '10:30 PM')
 *   viewerEndTime    the end in the viewer's zone, when the event has an end time
 *
 * Null when there is nothing to convert (no date, no time, no event zone).
 *
 * @param {object} event   events row
 * @param {string} [viewerZone]  defaults to the device's
 */
export function eventTimeDisplay(event, viewerZone = deviceTimeZone(), locale = undefined) {
  const tz = eventTimeZone(event);
  const startMs = eventStartMs(event);
  if (!tz || startMs == null || !parseTime(event?.event_time)) return null;

  const same = !viewerZone || sameClock(tz, viewerZone, startMs);
  const timeOpts = { hour: 'numeric', minute: '2-digit' };
  const hasEnd = !!parseTime(event?.end_time);

  return {
    sameClock: same,
    eventZone: tz,
    eventLabel: zoneLabel(tz, startMs),
    viewerZone: viewerZone || null,
    viewerLabel: viewerZone ? zoneLabel(viewerZone, startMs) : '',
    viewerDate: fmt(startMs, viewerZone, { weekday: 'short', month: 'short', day: 'numeric' }, locale),
    viewerTime: fmt(startMs, viewerZone, timeOpts, locale),
    viewerEndTime: hasEnd ? fmt(eventEndMs(event), viewerZone, timeOpts, locale) : null,
  };
}

/**
 * Zones offered when a host picks one: their own first, then the zones
 * invits' guests are mostly in. Each { zone, label, city }.
 */
export const COMMON_TIME_ZONES = [
  'Asia/Kolkata', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver', 'Europe/London',
  'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar',
  'Asia/Singapore', 'Asia/Kuala_Lumpur', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Kathmandu',
  'Asia/Dhaka', 'Asia/Karachi', 'Asia/Colombo', 'Australia/Sydney', 'Australia/Melbourne',
  'Australia/Perth', 'Pacific/Auckland', 'Africa/Johannesburg',
];

/** The picker's list, with `ownZone` first when it is not already in it. */
export function timeZoneOptions(ownZone, atMs = Date.now()) {
  const zones = [...COMMON_TIME_ZONES];
  if (ownZone && isValidTimeZone(ownZone) && !zones.some((z) => sameClock(z, ownZone, atMs) && zoneCity(z) === zoneCity(ownZone))) {
    zones.unshift(ownZone);
  }
  return zones.filter(isValidTimeZone).map((zone) => ({ zone, label: zoneLabel(zone, atMs), city: zoneCity(zone) }));
}
